import { useMemo, useState } from "react";

import { DurationLike } from "luxon";
import { getRecurringDatesByEndDate, getRecurringDatesByOccurrenceCount } from "../../shared/DateConversions";

export type RepeatBy = "endDate" | "count";
export enum RepeatInterval {
  "Monthly",
  "ThreeMonthly",
  "SixMonthly",
  "Yearly",
  "TwoYearly",
}

interface RecurrenceByEndDate {
  repeatBy: "endDate";
  repeatInterval: RepeatInterval;
  repeatEndDate?: Date | undefined;
}

interface RecurrenceByCount {
  repeatBy: "count";
  repeatInterval: RepeatInterval;
  repeatCount?: number | undefined;
}

export type RecurrenceSettings = RecurrenceByEndDate | RecurrenceByCount;

const RepeatIntervalDuration: Record<RepeatInterval, DurationLike> = {
  [RepeatInterval.Monthly]: { month: 1 },
  [RepeatInterval.ThreeMonthly]: { months: 3 },
  [RepeatInterval.SixMonthly]: { months: 6 },
  [RepeatInterval.Yearly]: { year: 1 },
  [RepeatInterval.TwoYearly]: { years: 2 },
};

interface TargetDates {
  targetStartDate: Date | undefined;
  targetCompletionDate: Date | undefined;
}

export interface RecurringTargetDates {
  recurrenceEnabled: boolean;
  setRecurrenceEnabled: (enabled: boolean) => void;
  recurrenceSettings: RecurrenceSettings | undefined;
  setTargetStartDate: (targetStartDate: Date | undefined) => void;
  setTargetCompletionDate: (targetCompletionDate: Date | undefined) => void;
  setRepeatBy: (repeatBy: RepeatBy) => void;
  setRepeatInterval: (repeatInterval: RepeatInterval) => void;
  setRepeatEndDate: (repeatEndDate: Date | undefined) => void;
  setRepeatCount: (repeatCount: number | undefined) => void;
  targetDates: TargetDates[];
}

export function useRecurringTargetDates() {
  const [targetStartDate, setTargetStartDate] = useState<Date | undefined>();
  const [targetCompletionDate, setTargetCompletionDate] = useState<Date | undefined>();
  const [recurrenceEnabled, setRecurrenceEnabled] = useState(false);
  const [repeatBy, setRepeatBy] = useState<RepeatBy>("endDate");
  const [repeatInterval, setRepeatInterval] = useState<RepeatInterval>(RepeatInterval.Monthly);
  const [repeatEndDate, setRepeatEndDate] = useState<Date | undefined>();
  const [repeatCount, setRepeatCount] = useState<number | undefined>(1);

  const recurrenceSettings = useMemo((): RecurrenceSettings | undefined => {
    switch (repeatBy) {
      case "count":
        return { repeatBy, repeatInterval, repeatCount };

      case "endDate":
        return { repeatBy, repeatInterval, repeatEndDate };
    }
  }, [repeatBy, repeatInterval, repeatEndDate, repeatCount]);

  const targetDates = useMemo(() => {
    let startDates: Date[] = [];
    let completionDates: Date[] = [];

    if (repeatBy === "endDate" && repeatEndDate) {
      startDates = targetStartDate
        ? getRecurringDatesByEndDate(targetStartDate, repeatEndDate, RepeatIntervalDuration[repeatInterval])
        : [];
      completionDates = targetCompletionDate
        ? getRecurringDatesByEndDate(targetCompletionDate, repeatEndDate, RepeatIntervalDuration[repeatInterval])
        : [];
    } else if (repeatBy === "count" && repeatCount) {
      startDates = targetStartDate
        ? getRecurringDatesByOccurrenceCount(targetStartDate, repeatCount, RepeatIntervalDuration[repeatInterval])
        : [];
      completionDates = targetCompletionDate
        ? getRecurringDatesByOccurrenceCount(targetCompletionDate, repeatCount, RepeatIntervalDuration[repeatInterval])
        : [];
    }

    let result: TargetDates[] = [];

    for (let i = 0; i < Math.max(startDates.length, completionDates.length); i++) {
      const targetStartDate = startDates[i] || undefined;
      const targetCompletionDate = completionDates[i] || undefined;

      result.push({ targetStartDate, targetCompletionDate });
    }

    return result;
  }, [targetStartDate, targetCompletionDate, repeatInterval, repeatEndDate, repeatCount, repeatBy]);

  return {
    targetStartDate,
    setTargetStartDate,
    targetCompletionDate,
    setTargetCompletionDate,
    recurrenceEnabled,
    setRecurrenceEnabled,
    recurrenceSettings,
    setRepeatBy,
    setRepeatInterval,
    setRepeatEndDate,
    setRepeatCount,
    targetDates,
  } as RecurringTargetDates;
}
